(() => {
  if (window.CPAnalytics?.version === '3.0.0') return;
  const productionPage = () => {
    const theme = window.CPThemeContext || window.Shopify?.theme || {};
    const role = String(theme.role || '').toLowerCase();
    return !window.Shopify?.designMode && !new URLSearchParams(location.search).has('preview_theme_id') && role === 'main';
  };
  const analyticsAllowed = () => { try { return productionPage() && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; } catch (_) { return false; } };
  // Only public content paths; never forward queries, checkout/account tokens or form values.
  const pathOnly = value => {
    try {
      const url = new URL(value, location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';
      const path = url.pathname;
      return /^\/(?:$|(?:pages|products|collections|blogs|policies)\/[a-zA-Z0-9_\/-]+$|(?:cart|search)\/?$)/.test(path) ? path.slice(0, 160) : '';
    } catch (_) { return ''; }
  };
  const tokenKeys = new Set('intent lead_source placement step tool tool_id calculator product variant_id product_id purchase_mode coverage_result recommendation_type need context source result drink style brew route tenure usage_band selected_jobs document_type filter option manufacturer collection action error_code field_type'.split(' '));
  const numberKeys = new Set('rank products alternatives quantity qty packs days_per_week items count results_count progress_percent'.split(' '));
  const flagKeys = new Set(['available']);
  const intents = new Set('advice quote rental installation service fault ingredients cashless documents general sample_request installation-check replacement solution-builder project-quote coverage'.split(' '));
  const safeToken = value => typeof value === 'string' && /^[a-zA-Z0-9 _.:/-]{1,100}$/.test(value) ? value : '';
  const cleanParams = params => {
    const out = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (['page', 'link_path'].includes(key)) { const path = pathOnly(value); if (path) out[key] = path; }
      else if (key === 'intent') { if (intents.has(value)) out[key] = value; }
      else if (tokenKeys.has(key)) { const token = safeToken(String(value ?? '')); if (token) out[key] = token; }
      else if (numberKeys.has(key) && typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000000) out[key] = value;
      else if (flagKeys.has(key) && typeof value === 'boolean') out[key] = value;
    });
    return out;
  };
  const shopifyQueue = [];
  let retryTimer, inFlight = 0, sequence = 0, consentGeneration = 0;
  const delivery = { accepted: 0, expired: 0 };
  const clearQueue = () => { shopifyQueue.length = 0; clearTimeout(retryTimer); retryTimer = null; consentGeneration++; };
  const scheduleFlush = () => {
    if (!retryTimer && shopifyQueue.length) retryTimer = setTimeout(() => { retryTimer = null; flushShopifyQueue(); }, 800);
  };
  const publishShopify = (event, params) => {
    if (shopifyQueue.length + inFlight >= 60) { delivery.expired++; return false; }
    shopifyQueue.push({ event, params, at: Date.now(), attempts: 0, generation: consentGeneration });
    flushShopifyQueue();
    return true;
  };
  const flushShopifyQueue = () => {
    if (!analyticsAllowed()) { clearQueue(); return; }
    const publish = window.Shopify?.analytics?.publish;
    const now = Date.now();
    shopifyQueue.splice(0).forEach(item => {
      if (now - item.at > 30000 || item.attempts >= 4) { delivery.expired++; return; }
      if (typeof publish !== 'function') { shopifyQueue.push(item); return; }
      item.attempts++; inFlight++;
      const settled = accepted => {
        inFlight--;
        if (item.generation !== consentGeneration || !analyticsAllowed()) return;
        if (accepted) delivery.accepted++;
        else if (item.attempts < 4 && Date.now() - item.at < 30000 && shopifyQueue.length < 60) shopifyQueue.push(item);
        else delivery.expired++;
        scheduleFlush();
      };
      try { Promise.resolve(publish.call(window.Shopify.analytics, 'cafe_perfection:' + item.event, item.params)).then(result => settled(result !== false), () => settled(false)); }
      catch (_) { settled(false); }
    });
    scheduleFlush();
  };
  const track = (event, params = {}) => {
    if (!analyticsAllowed() || !/^cp_[a-z0-9_]{1,36}$/.test(event)) return false;
    // Standard ecommerce belongs exclusively to the Google & YouTube app.
    if (['cp_page_view','cp_view_item','cp_add_to_cart','cp_begin_checkout','cp_purchase'].includes(event)) return false;
    if (event === 'cp_enquiry_submitted' && params.lead_source !== 'shopify_contact_form') return false;
    const context = window.CPThemeContext || {};
    const clean = { ...cleanParams(params), event_source: 'cp_theme', schema_version: '3.0.0', event_id: Date.now().toString(36) + '-' + (++sequence) + '-' + Math.random().toString(36).slice(2, 9), page: pathOnly(location.pathname) || '/', page_type: safeToken(context.pageType || ''), season: safeToken(context.season || 'everyday') };
    // Keep the existing local dataLayer event for backwards compatibility, but publish
    // the authoritative event through Shopify Customer Events so a consent-aware
    // custom pixel can forward B2B/tool conversions to GA4 without duplicating Shopify's
    // standard ecommerce events from the Google & YouTube app.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...clean });
    publishShopify(event, clean);
    document.dispatchEvent(new CustomEvent('cp:analytics', { detail: { event, params: clean } }));
    return true;
  };
  window.CPAnalytics = Object.freeze({ version: '3.0.0', track, flush: flushShopifyQueue, status: () => ({ version: '3.0.0', expectedMeasurementId: 'G-79C186R99S', production: productionPage(), analyticsAllowed: analyticsAllowed(), queued: shopifyQueue.length, inFlight, acceptedByShopify: delivery.accepted, expired: delivery.expired, ga4ReceiptVerified: false }) });

  const params = new URLSearchParams(location.search);
  const allowed = ['utm_source','utm_medium','utm_campaign','utm_content'];
  const source = {};
  const campaignToken = value => typeof value === 'string' && /^[a-zA-Z0-9 ._-]{1,100}$/.test(value) ? value : '';
  const refreshSource = () => {
    Object.keys(source).forEach(key => delete source[key]);
    if (!analyticsAllowed()) {
      try { [...allowed, 'utm_term', 'gclid', 'landing', 'contact_pending'].forEach(key => sessionStorage.removeItem('cp_' + key)); } catch (_) {}
      document.querySelectorAll('input[name="contact[Landing page]"],input[name="contact[Campaign source]"],input[name="contact[Campaign medium]"],input[name="contact[Campaign]"]').forEach(input => { if (input.type === 'hidden') input.remove(); });
      return;
    }
    allowed.forEach(key => {
      let value = campaignToken(params.get(key));
      try { if (value) sessionStorage.setItem('cp_' + key, value); else value = campaignToken(sessionStorage.getItem('cp_' + key)); } catch (_) {}
      if (value) source[key] = value;
    });
    try { sessionStorage.removeItem('cp_gclid'); sessionStorage.removeItem('cp_utm_term'); source.landing = pathOnly(sessionStorage.getItem('cp_landing') || location.pathname); sessionStorage.setItem('cp_landing', source.landing); } catch (_) { source.landing = location.pathname; }
  };
  refreshSource();

  const addHidden = (form, name, value) => { if (!value || form.querySelector(`[name="${name}"]`)) return; const input = document.createElement('input'); input.type='hidden'; input.name=name; input.value=String(value).slice(0, /brief/i.test(name) ? 7000 : 220); form.appendChild(input); };
  const finalIntent = form => {
    if (form.closest('[data-cp-enquiry-root]')) return form.querySelector('[data-enquiry-field="type"]')?.value || 'general';
    const contact = form.closest('[data-cp-contact-v14]');
    return contact?.dataset.contactIntent || form.querySelector('[data-contact-intent]')?.value || new URLSearchParams(location.search).get('intent') || 'general';
  };
  const setHidden = (form, name, value) => {
    addHidden(form, name, value);
    const input = form.querySelector(`[name="${name}"]`);
    if (input && input.type === 'hidden') input.value = String(value || '').slice(0, 220);
  };
  const confirmContact = form => {
    const successful = form.querySelector('[data-cp-contact-success]');
    const rejected = form.querySelector('[data-cp-contact-error]');
    if (!analyticsAllowed() || (!successful && !rejected) || form.dataset.cpOutcomeTracked) return;
    form.dataset.cpOutcomeTracked = '1';
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem('cp_contact_pending') || 'null'); sessionStorage.removeItem('cp_contact_pending'); } catch (_) {}
    if (!pending || Date.now() - pending.at > 1800000 || pending.path !== location.pathname) return;
    track(successful ? 'cp_enquiry_submitted' : 'cp_enquiry_failed', { lead_source: 'shopify_contact_form', page: location.pathname, intent: pending.intent });
  };
  const readJson = (storage,key,fallback) => { try { return JSON.parse(window[storage].getItem(key) || '') || fallback; } catch(e) { return fallback; } };
  const projectBrief = () => {
    const data = readJson('sessionStorage','cp_project_v2',null);
    if (!data || !Array.isArray(data.items) || !data.items.length) return '';
    const head = ['Café Perfection commercial project' + (data.name ? ': ' + data.name : '')];
    data.items.forEach((x,i) => head.push(`${i+1}. ${x.qty || 1} × ${x.title || 'Equipment'}${x.site ? ' — Site: '+x.site : ''}${x.price ? ' — '+x.price : ''}${x.note ? ' — Note: '+x.note : ''}`));
    return head.join('\n').slice(0,7000);
  };
  const enrichForms = root => {
    const handoff = window.CPIntelligence?.getHandoff?.() || null;
    const currentIntent = params.get('intent') || '';
    const useHandoff = !!(handoff && handoff.brief && handoff.extra?.source !== 'cp-flow' && (currentIntent === 'solution-builder' || currentIntent === 'project-quote' || currentIntent === 'quote' || params.get('source') === 'commercial-intelligence' || params.get('handoff') === '1'));
    const pBrief = currentIntent === 'project-quote' ? projectBrief() : '';
    (root || document).querySelectorAll('form[action*="/contact"]').forEach(form => {
      if (form.querySelector('[name="form_type"]')?.value === 'customer') return;
      addHidden(form,'contact[Current page]',location.pathname);
      addHidden(form,'contact[Landing page]',source.landing);
      addHidden(form,'contact[Campaign source]',source.utm_source);
      addHidden(form,'contact[Campaign medium]',source.utm_medium);
      addHidden(form,'contact[Campaign]',source.utm_campaign);
      addHidden(form,'contact[Product interest]',params.get('product'));
      addHidden(form,'contact[Enquiry intent]',params.get('intent'));
      if (useHandoff && !form.closest('[data-cp-enquiry-root]')) addHidden(form,'contact[Planning brief]',handoff.brief);
      if (pBrief && !form.closest('[data-cp-enquiry-root]')) addHidden(form,'contact[Project brief]',pBrief);
      const body = form.querySelector('textarea[name="contact[body]"],textarea[name="contact[Body]"]');
      if (body && !form.closest('[data-cp-enquiry-root]') && useHandoff && !body.dataset.cpBriefAdded) { body.value = 'Planning brief from the Café Perfection website:\n\n' + handoff.brief + '\n\n' + body.value; body.dataset.cpBriefAdded = '1'; }
      if (body && !form.closest('[data-cp-enquiry-root]') && !body.value.trim() && pBrief) body.value = pBrief;
      confirmContact(form);
    });
  };

  const removeKnownGeneratedFiller = root => {
    (root || document).querySelectorAll('.cp-product__body strong, .rte strong').forEach(strong => {
      if ((strong.textContent || '').trim().toLowerCase() !== 'specifications:') return;
      const heading = strong.closest('p');
      const list = heading && heading.nextElementSibling;
      if (!heading || !list || list.tagName !== 'UL') return;
      const text = (list.textContent || '').replace(/\s+/g,' ').trim().toLowerCase();
      if (text.includes('power: commercial grade') || (text.includes('voltage: 220–240v') && text.includes('use: office / hospitality'))) {
        list.remove(); heading.remove();
      }
    });
  };

  const reveal = root => {
    const items = (root || document).querySelectorAll('[data-cp-reveal]:not([data-cp-observed])');
    if (!('IntersectionObserver' in window)) { items.forEach(x => x.style.opacity='1'); return; }
    document.documentElement.classList.add('cp-reveal-ready');
    const io = new IntersectionObserver(entries => entries.forEach(e => { if(e.isIntersecting){e.target.classList.add('is-visible');io.unobserve(e.target);} }),{threshold:.08,rootMargin:'0px 0px -30px'});
    items.forEach(x => { x.dataset.cpObserved='1'; io.observe(x); });
  };

  const productHealth = root => {
    if (!window.Shopify?.designMode) return;
    const product = (root || document).querySelector('[data-cp-product]');
    if (!product || product.querySelector('[data-cp-health]')) return;
    const mediaCount = product.querySelectorAll('[data-cp-thumb]').length + (product.querySelector('.cp-product__main-image') ? 1 : 0);
    const hasFallback = !!product.querySelector('.cp-product__fallback');
    const hasFacts = !product.querySelector('.cp-product__facts-empty');
    const hasPdf = !!product.querySelector('[data-cp-event="spec_download"]');
    const issues = [];
    if (hasFallback || mediaCount === 0) issues.push('Needs product media');
    if (!hasFacts) issues.push('Needs verified structured specs');
    if (!hasPdf) issues.push('No verified product document is linked yet');
    if (product.dataset.purchaseMode === 'quote' && product.querySelector('form.cp-product__form button[type="submit"]:not(:disabled)')) issues.push('Quote-only product unexpectedly has an enabled purchase button');
    const planner = product.querySelector('[data-cp-replenishment]');
    if (planner && !Number(planner.dataset.packSize) && !Number(planner.dataset.servings) && !/\d+(?:[.,]\d+)?\s*(kg|g|ml|l)\b/i.test(planner.dataset.productTitle || '')) issues.push('Planner needs verified pack or servings data');
    const panel = document.createElement('aside');
    panel.dataset.cpHealth='1';
    panel.setAttribute('aria-label','V2 product health');
    panel.style.cssText='position:fixed;z-index:9999;right:16px;bottom:16px;width:min(340px,calc(100vw - 32px));padding:16px 18px;border:1px solid rgba(201,168,76,.55);border-radius:18px;background:#062f31;color:#fff;box-shadow:0 18px 55px rgba(0,0,0,.3);font:13px/1.45 -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif';
    panel.innerHTML='<strong style="display:block;color:#e2c56d;margin-bottom:7px">V2 Product Health · Theme editor only</strong><div style="opacity:.9">'+(issues.length ? issues.map(x=>'• '+x).join('<br>') : '✓ Core product presentation data is present')+'</div>';
    document.body.appendChild(panel);
  };

  const init = root => { enrichForms(root); removeKnownGeneratedFiller(root); reveal(root); productHealth(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);

  // At document bubbling phase, the form's own validators have already run.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches?.('form[action*="/contact"]') || form.querySelector('[name="form_type"]')?.value === 'customer' || event.defaultPrevented || !form.checkValidity()) return;
    const intent = finalIntent(form);
    setHidden(form, 'contact[Enquiry intent]', intent);
    try { if (analyticsAllowed()) sessionStorage.setItem('cp_contact_pending', JSON.stringify({ path: location.pathname, intent, at: Date.now() })); } catch (_) {}
    track('cp_enquiry_attempt', { lead_source: 'shopify_contact_form', intent });
  });
  const startedForms = new WeakSet();
  document.addEventListener('input', event => {
    const form = event.target.closest?.('form[action*="/contact"]');
    if (!form || form.querySelector('[name="form_type"]')?.value === 'customer' || form.closest('[data-cp-enquiry-root]') || startedForms.has(form)) return;
    if (track('cp_enquiry_started', { lead_source: 'contact_form', intent: finalIntent(form) })) startedForms.add(form);
  });
  const invalidForms = new WeakMap();
  document.addEventListener('invalid', event => {
    const form = event.target.closest?.('form[action*="/contact"]');
    if (!form || form.querySelector('[name="form_type"]')?.value === 'customer' || Date.now() - (invalidForms.get(form) || 0) < 2000) return;
    invalidForms.set(form, Date.now());
    track('cp_enquiry_validation_error', { lead_source: 'shopify_contact_form', intent: finalIntent(form), error_code: 'invalid_field' });
  }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.name?.startsWith('filter.') && input.closest('facet-filters-form')) {
      track('cp_catalogue_filter', { filter: input.name, option: ['checkbox','radio'].includes(input.type) ? (input.checked ? 'selected' : 'cleared') : 'changed' });
    } else if (input.name === 'sort_by') track('cp_catalogue_sort', { option: input.value });
  });
  document.addEventListener('submit', event => {
    if (!event.defaultPrevented && event.target.matches?.('form[action*="/search"]')) track('cp_site_search', { source: 'storefront_search' });
  });

  document.addEventListener('click', e => {
    if (e.button > 0) return;
    const explicit = e.target.closest('[data-cp-event]');
    if (explicit && !explicit.closest('[data-cp-product]')) track('cp_'+explicit.dataset.cpEvent,{page:location.pathname,link_path:explicit.getAttribute('href')||'',placement:explicit.dataset.cpPlacement||'content'});
    const relationship = e.target.closest('[data-cp-lifecycle] .cp-partnership__steps a');
    if (relationship) track('cp_relationship_step_click', { step: relationship.closest('li')?.dataset.cpStep || '', link_path: relationship.getAttribute('href') || '', page: location.pathname });
    const link = e.target.closest('a[href]'); if (!link) return;
    const href = link.getAttribute('href') || '', text = (link.textContent || '').trim();
    if (link.closest('#product-grid,.product-grid') && /\/products\//.test(href)) track('cp_catalogue_product_click',{link_path:href,placement:'product_grid'});
    if (/^tel:/i.test(href)) track('cp_phone_click',{lead_source:'storefront_phone_link',page:location.pathname,placement:link.dataset.cpPlacement||(link.closest('header')?'header':link.closest('footer')?'footer':'content')});
    else if (/^mailto:/i.test(href)) track('cp_email_click',{lead_source:'storefront_email_link',page:location.pathname,placement:link.dataset.cpPlacement||(link.closest('header')?'header':link.closest('footer')?'footer':'content')});
    else if (!e.defaultPrevented && /contact|quote|consultation|survey|enquir/i.test(href + ' ' + text)) track('cp_enquiry_started',{lead_source:'storefront_cta',link_path:href,cta_text:text,page:location.pathname});
    else if (/ingredients|cleaning-care|milk-powders|coffee-beans/.test(href)) track('cp_commerce_path',{link_path:href,cta_text:text,page:location.pathname});
    if ((!explicit || !/download/.test(explicit.dataset.cpEvent)) && (/\.(?:pdf|docx?|xlsx?|csv|zip)(?:$|[?#])/i.test(href) || link.hasAttribute('download'))) track('cp_resource_download',{link_path:href,page:location.pathname,document_type:'download'});
  },{passive:true});

  document.addEventListener('shopify:section:load',e => init(e.target));
  let seasonTracked = false;
  const privacyReady = () => {
    refreshSource();
    if (!analyticsAllowed()) return;
    enrichForms(document);
    const season = window.CPThemeContext?.season;
    if (!seasonTracked && season && season !== 'everyday' && season !== 'off') { seasonTracked = track('cp_season_view', { placement: 'storefront' }); }
  };
  document.addEventListener('visitorConsentCollected', () => { privacyReady(); flushShopifyQueue(); });
  document.addEventListener('DOMContentLoaded', flushShopifyQueue, { once: true });
  window.addEventListener('pageshow', flushShopifyQueue, { once: true });
  privacyReady();
  flushShopifyQueue();
  setTimeout(flushShopifyQueue, 500);
  setTimeout(flushShopifyQueue, 1800);
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) { privacyReady(); flushShopifyQueue(); } }); } catch (_) {}
  }
})();
